// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { addCart, AddCartApiParameter } from "@insite/client-framework/Services/CartService";
import { GetVmiLocationsApiParameter } from "@insite/client-framework/Services/VmiLocationService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState, isCartEmpty } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import loadVmiLocations from "@insite/client-framework/Store/Data/VmiLocations/Handlers/LoadVmiLocations";
import { getVmiLocationsDataView } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { setCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const NavigationModeCookieName = "NavigationMode";
export const NavigationModeState = "NavigationModeState";

interface OwnProps {
    extendedStyles?: NavigationModeMenuStyles;
    // XNG-Changes
    afterSwitchingAction?: () => void;
}

const getVmiLocationsParameter = {
    filter: "",
    sort: "Name",
    page: 1,
    pageSize: 1, // get first location
} as GetVmiLocationsApiParameter;

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const vmiEnabled = getSettingsCollection(state).orderSettings.vmiEnabled;
    const cart = getCurrentCartState(state).value;

    return {
        currentMode: getCookie(NavigationModeCookieName) || "Storefront",
        displayModeSwitch:
            isAuthenticated &&
            vmiEnabled &&
            session.userRoles &&
            (session.userRoles.toLowerCase().indexOf("vmi_admin") !== -1 ||
                session.userRoles.toLowerCase().indexOf("vmi_user") !== -1),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        cartPageLink: getPageLinkByPageType(state, "CartPage")?.url,
        cart,
        isCartEmpty: isCartEmpty(cart),
        vmiLocationsDataView: getVmiLocationsDataView(state, getVmiLocationsParameter),
        getVmiLocationsParameter,
        selectedLocationId: state.pages.vmiBins.getVmiBinsParameter.vmiLocationId,
        cartRequestInProcess: state.pages.vmiBins.cartRequestInProcess,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    };
};

const mapDispatchToProps = {
    removeCart,
    loadVmiLocations,
    loadCurrentCart,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface NavigationModeMenuStyles {
    currencyWrapper?: InjectableCss;
    navigationModeSelect?: SelectPresentationProps;
    navigationConfirmModal?: TwoButtonModalStyles;
    navigationConfirmMessage?: TypographyProps;
    navigationConfirmQuestion?: TypographyProps;
    // XNG-Changes
    toggleInput?: CheckboxPresentationProps;
}

export const navigationModeMenuStyles: NavigationModeMenuStyles = {
    currencyWrapper: {
        css: css`
            display: flex;
        `,
    },
    navigationModeSelect: {
        backgroundColor: "common.accent",
        cssOverrides: {
            formInputWrapper: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        null,
                        css`
                            width: 118px;
                        `,
                        css`
                            width: 271px;
                        `,
                    ])}
            `,
            inputSelect: css`
                border: none;
                text-transform: uppercase;
            `,
        },
    },
    navigationConfirmMessage: {
        as: "p",
        css: css`
            padding-top: 5px;
        `,
    },
    navigationConfirmQuestion: {
        as: "p",
        css: css`
            padding-top: 20px;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const NavigationModeMenu = ({
    currentMode,
    displayModeSwitch,
    vmiDashboardPageUrl,
    homePageUrl,
    cartPageLink,
    cart,
    isCartEmpty,
    history,
    removeCart,
    extendedStyles,
    loadVmiLocations,
    vmiLocationsDataView,
    getVmiLocationsParameter,
    loadCurrentCart,
    selectedLocationId,
    cartRequestInProcess,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    afterSwitchingAction,
}: Props) => {
    const styles = useMergeStyles("navigationModeMenu", navigationModeMenuStyles, extendedStyles);
    const [navigationHeaderText, setNavigationHeaderText] = useState<string>("");
    const [navigationMessage, setNavigationMessage] = useState<JSX.Element>();
    const [navigationCurrentMode, setNavigationCurrentMode] = useState<string>(currentMode);
    const [navigationNewMode, setNavigationNewMode] = useState<string>(currentMode);
    const [confirmModalIsOpen, setConfirmModalIsOpen] = useState<boolean>(false);

    // XNG-Changes: updating function to handle toggle input
    const navigationModeChangeHandler = (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        const newValue = value ? "Vmi" : "Storefront";

        if (!isReorderConfirmationModalOpen && hasChangeLastQty && newValue !== "Vmi") {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: () => switchNavigationMode(newValue) });
            // XNG-Changes
            afterSwitchingAction?.();
            return;
        }

        switchNavigationMode(newValue);
    };

    const switchNavigationMode = (mode: string) => {
        if (isCartEmpty) {
            setCookie(NavigationModeCookieName, mode);
            setNavigationCurrentMode(mode);

            if (mode === "Vmi") {
                vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
            } else if (cart?.status === "AlternateCart") {
                deleteAlternateCart();
            } else {
                homePageUrl && history.push(homePageUrl);
            }

            // XNG-Changes
            afterSwitchingAction?.();
            return;
        }

        if (mode === "Vmi") {
            setCookie(NavigationModeCookieName, currentMode);
            setConfirmModalIsOpen(true);
            // XNG-Changes
            afterSwitchingAction?.();
            setNavigationHeaderText(translate("Continue to VMI"));
            setNavigationMessage(
                <>
                    <Typography {...styles.navigationConfirmMessage}>{siteMessage("Cart_Change_To_Vmi")}</Typography>
                    <Typography {...styles.navigationConfirmQuestion}>
                        {translate("Would you like to continue?")}
                    </Typography>
                </>,
            );
        } else {
            setCookie(NavigationModeCookieName, mode);
            setNavigationCurrentMode(mode);
            if (cart?.status === "AlternateCart") {
                deleteAlternateCart();
            }
            homePageUrl && history.push(homePageUrl);
        }

        setNavigationNewMode(mode);
    };

    const deleteAlternateCart = () => {
        removeCart({
            cartId: API_URL_CURRENT_FRAGMENT,
            isAlternateCart: true,
            onSuccess: () => {
                homePageUrl && history.push(homePageUrl);
                loadCurrentCart();
            },
        });
    };

    const createAlternateCart = () => {
        loadVmiLocations({
            ...getVmiLocationsParameter,
            onComplete: ({ apiResult }) => {
                if (!apiResult?.vmiLocations || apiResult.vmiLocations.length === 0) {
                    return;
                }

                const locationId = selectedLocationId ?? apiResult.vmiLocations[0].id;
                addCart({ vmiLocationId: locationId } as AddCartApiParameter).then(() => {
                    loadCurrentCart();
                });
            },
        });
    };

    const changeNavigationModeHandler = () => {
        setCookie(NavigationModeCookieName, navigationNewMode);
        setNavigationCurrentMode(navigationNewMode);

        if (navigationNewMode === "Vmi") {
            createAlternateCart();
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else {
            homePageUrl && history.push(homePageUrl);
        }

        setConfirmModalIsOpen(false);
        // XNG-Changes
        afterSwitchingAction?.();
    };

    const cancelNavigationModeHandler = () => {
        if (!isCartEmpty && navigationCurrentMode === "Storefront") {
            cartPageLink && history.push(cartPageLink);
        }
        setConfirmModalIsOpen(false);
    };

    const closeNavigationModeHandler = () => {
        setConfirmModalIsOpen(false);
    };

    if (!displayModeSwitch) {
        return null;
    }

    const menuId = "navigationModeMenu";

    // XNG-Changes: Structure Update
    return (
        <StyledWrapper {...styles.currencyWrapper}>
            <VisuallyHidden as="label" id={`${menuId}-label`} htmlFor={menuId}>
                {translate("Navigation Mode")}
            </VisuallyHidden>
            {/* <Select
                {...styles.navigationModeSelect}
                disabled={cartRequestInProcess}
                uid={menuId}
                onChange={navigationModeChangeHandler}
                value={navigationCurrentMode}
                autoComplete="off"
                hasLabel
            >
                <option value="Storefront" key="Storefront">
                    {translate("Storefront")}
                </option>
                <option value="Vmi" key="Vendor Managed Inventory">
                    {translate("Vendor Managed Inventory")}
                </option>
            </Select> */}
            <Checkbox
                variant="toggle"
                checked={navigationCurrentMode === "Vmi"}
                onChange={navigationModeChangeHandler}
                value={navigationCurrentMode}
                {...styles.toggleInput}
            >
                {translate("VMI Mode")}
            </Checkbox>
            <TwoButtonModal
                headlineText={`${navigationHeaderText}`}
                {...styles.navigationConfirmModal}
                modalIsOpen={confirmModalIsOpen}
                messageText={navigationMessage}
                cancelButtonText={translate("Back to Cart")}
                submitButtonText={translate("Continue")}
                onCancel={cancelNavigationModeHandler}
                onSubmit={changeNavigationModeHandler}
                onClose={closeNavigationModeHandler}
                submitTestSelector="submitNavigationMode"
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(NavigationModeMenu));
